import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo } from 'react'
import { SectionsContextType, SectionsProviderProps } from 'components/Wizards/Wizard/Form/Sections/Types'
import { useFields } from 'components/Wizards/Wizard/Form/useFields'
import { useFormikContext } from 'formik'
import { useCreateFieldsetCompletion } from 'hooks/queries/workspaces/useCreateFieldsetCompletion'
import { FormStatus } from 'components/Wizards/Wizard/Form/Types'
import { FormikErrors } from 'formik/dist/types'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { PollFieldsetCompletion } from 'components/PollFieldsetCompletion/PollFieldsetCompletion'
import { AIJobStatus, FieldsetCompletion } from 'types'
import { useAutoCompleteFields } from 'components/Wizards/Wizard/Form/useAutoCompleteFields'
import { WizardType } from 'components/Wizards/schemas/Types'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'

const SectionsContext = createContext<SectionsContextType>({
  isAutoFillSectionsEnabled: true,
  mainFields: [],
  manualFields: [],
  additionalFields: [],
  collapsedFields: [],
  createFieldsetCompletion: () => {},
  type: GenerationType.Brainstorm,
  isAutocompleteEnabled: true,
})

const AUTOFILL_EXCLUDE_LIST = ['source_material_ids', 'language']

export const SectionsProvider = ({ schema, children, onAutoFill }: PropsWithChildren<SectionsProviderProps>) => {
  const { status, values, setStatus, setFieldValue, initialValues } = useFormikContext<{ [key: string]: any }>()
  const {
    meta: { type, autofillAutomatically },
  } = schema.describe()
  let { manualFields, additionalFields, collapsedFields, mainFields, skipResetFields } = useFields({ schema })

  const isAutocompleteEnabled = !([GenerationType.Brainstorm, GenerationType.ContentPlan] as WizardType[]).includes(
    type as WizardType,
  )

  mainFields = isAutocompleteEnabled ? mainFields : manualFields.concat(mainFields)
  manualFields = isAutocompleteEnabled ? manualFields : []

  const isAutoFillSectionsEnabled =
    !isAutocompleteEnabled ||
    [FormStatus.ManuallyFilled, FormStatus.AutoFilling, FormStatus.AutoFilled].includes(status)
  const { mutate, data } = useCreateFieldsetCompletion()
  const autoCompleteFields = useAutoCompleteFields()

  const completedFields = useMemo(() => {
    const valueEntries = Object.entries(values)
    const nonEmptyValues = valueEntries.filter(([_key, value]) => {
      if (Array.isArray(value)) {
        return value.length !== 0
      }
      return value !== '' && value !== null
    })
    return Object.fromEntries(nonEmptyValues)
  }, [values])

  useEffect(() => {
    if (autofillAutomatically) {
      createFieldsetCompletion()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const createFieldsetCompletion = useCallback(() => {
    setStatus(FormStatus.AutoFilling)
    const resetPromises: Array<Promise<void | FormikErrors<{ [key: string]: any }>>> = []
    mainFields.forEach(([field]) => {
      if (!skipResetFields.find(([name]) => name === field)) {
        resetPromises.push(setFieldValue(field, initialValues[field]))
      }
    })
    return Promise.all(resetPromises).then(() => {
      return mutate(
        {
          fields: [
            ...mainFields.filter(([name]) => !AUTOFILL_EXCLUDE_LIST.includes(name)).map(([name]) => name),
            'seo_words',
          ],
          completedFields,
          generationType: type || 'blog_post',
        },
        { onError: () => setStatus(undefined) },
      )
    })
  }, [completedFields]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleComplete = useCallback(
    (fieldsetCompletion: FieldsetCompletion) => {
      if (fieldsetCompletion.status === AIJobStatus.Failed) {
        showErrorMessageToast(
          'Blaze was unable to autofill. Please try again and contact support if this issue persists.',
        )
        setStatus(FormStatus.ManuallyFilled)
        return
      }
      autoCompleteFields(fieldsetCompletion.completed_fields).finally(async () => {
        await onAutoFill?.(fieldsetCompletion.completed_fields)
        setStatus(FormStatus.AutoFilled)
      })
    },
    [autoCompleteFields], // eslint-disable-line react-hooks/exhaustive-deps
  )

  return (
    <SectionsContext.Provider
      value={{
        isAutoFillSectionsEnabled,
        manualFields,
        mainFields,
        additionalFields,
        collapsedFields,
        fieldsetCompletion: data?.fieldset_completion,
        createFieldsetCompletion,
        type,
        isAutocompleteEnabled,
      }}
    >
      <>
        <PollFieldsetCompletion fieldsetCompletionId={data?.fieldset_completion.id} onComplete={handleComplete} />
        {children}
      </>
    </SectionsContext.Provider>
  )
}

export const useSections = () => useContext(SectionsContext)
